// =============================================================================
// perfil-regiao-dom.ts — helpers de DOM locais da Secao 02 (Perfil da Regiao).
//
// Proveniencia: replica o comportamento exato de UI.qsa / UI.bindOnce /
// UI.syncInlineTabs (assets/unified-app.js 822-846), mas LOCAL ao modulo para
// nao acoplar a view ao global `UI` (dono: integracao). Mantem fidelidade de
// estilos inline das tabs e a semantica de "bind once" por chave.
//
// Por que replicar e nao importar UI: o contrato so expoe UiHelper.qsa como
// `readonly Element[]`; aqui preciso de elementos tipados (HTMLInputElement nos
// sliders) e da formatacao inline de tab identica ao original. Replicar e DRY
// dentro do escopo da view e evita dependencia circular com o chrome.
// =============================================================================

/**
 * Coleta elementos por seletor dentro de um escopo (default: document).
 * Espelha UI.qsa: Array.from(querySelectorAll). Tipado por generico greppavel.
 * @param selector seletor CSS a consultar.
 * @param scope raiz da busca; document por padrao.
 */
export function queryPerfilNodes<TElement extends Element = Element>(
  selector: string,
  scope: ParentNode = document,
): TElement[] {
  return Array.prototype.slice.call(scope.querySelectorAll(selector)) as TElement[];
}

/** Propriedade-flag injetada no elemento para impedir bind duplicado (UI.bindOnce). */
type BindOnceFlagged = Element & Record<string, boolean | undefined>;

/**
 * Liga um listener UMA unica vez por (evento, chave), espelhando UI.bindOnce.
 * Marca o elemento com `__bound_<evento>_<chave>` — reentrante e idempotente,
 * essencial porque activate() pode rodar a cada entrada na secao (scroll-spy).
 * @param target elemento alvo; no-op se null.
 * @param eventName nome do evento DOM (ex.: 'click', 'input').
 * @param bindKey chave logica que distingue este bind de outros no mesmo elemento.
 * @param listener callback a registrar.
 */
export function bindPerfilListenerOnce(
  target: Element | null,
  eventName: string,
  bindKey: string,
  listener: EventListener,
): void {
  if (!target) return;
  const flagName = `__bound_${eventName}_${bindKey}`;
  const flagged = target as BindOnceFlagged;
  if (flagged[flagName]) return;
  flagged[flagName] = true;
  target.addEventListener(eventName, listener);
}

/**
 * Aplica os estilos inline de tab ativa/inativa (UI.syncInlineTabs, 838-846).
 * Borda azul #185FA5 na ativa, transparente nas demais; cor/peso conforme estado.
 * Replicado FIEL para preservar a aparencia das u-perfil-tab.
 * @param tabButtons todos os botoes de tab da faixa.
 * @param activeTab tab a marcar como ativa; se null, usa quem ja tem .active.
 */
export function syncPerfilInlineTabs(
  tabButtons: readonly HTMLElement[],
  activeTab: HTMLElement | null,
): void {
  tabButtons.forEach((tabButton) => {
    const isActive = activeTab
      ? tabButton === activeTab
      : tabButton.classList.contains('active');
    tabButton.classList.toggle('active', isActive);
    tabButton.style.borderBottom = isActive ? '2px solid #185FA5' : '2px solid transparent';
    tabButton.style.color = isActive ? '#185FA5' : 'var(--color-text-secondary)';
    tabButton.style.fontWeight = isActive ? '500' : '400';
    tabButton.style.background = 'transparent';
  });
}
